import React, { useState, useMemo } from 'react';
import {
  Box,
  Popover,
  TextField,
  InputAdornment,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { Search, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import countryCodes from '../../data/countryCodes.json';
import { EmojiFlag, checkEmojiFlagSupport } from './EmojiFlag';

const CountryCodePicker = ({ countryCode, onChange, height = '40px' }) => {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');

  // Filtered country list — recalculates only when search query changes
  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return countryCodes;
    return countryCodes.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.dial_code.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [countrySearch]);

  return (
    <>
      <Box
        onClick={(e) => { setPickerAnchor(e.currentTarget); setPickerOpen(true); }}
        sx={{
          width: { xs: 100, sm: 120 },
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 1.5 },
          height: height,
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'}`,
          borderRadius: '8px',
          cursor: 'pointer',
          bgcolor: 'background.paper',
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: theme.palette.text.primary,
          },
          userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '4px', sm: '6px' } }}>
          <EmojiFlag emoji={countryCode?.emoji} code={countryCode?.code} />
          <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, fontWeight: 600, letterSpacing: '0.3px' }}>
            {countryCode?.dial_code}
          </Typography>
        </Box>
        {pickerOpen
          ? <KeyboardArrowUp sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, opacity: 0.6 }} />
          : <KeyboardArrowDown sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, opacity: 0.6 }} />
        }
      </Box>

      {/* Popover dropdown */}
      <Popover
        open={pickerOpen}
        anchorEl={pickerAnchor}
        onClose={() => { setPickerOpen(false); setCountrySearch(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 300,
            mt: '4px',
            borderRadius: '10px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          }
        }}
      >
        {/* Pinned search input */}
        <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search country..."
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: '1rem', opacity: 0.45 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.85rem' },
            }}
          />
        </Box>

        {/* Scrollable country list */}
        <Box
          sx={{
            maxHeight: 240,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(128,128,128,0.5) transparent',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(128,128,128,0.5)',
              borderRadius: '10px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(128,128,128,0.8)',
              backgroundClip: 'content-box',
            },
          }}
        >
          {filteredCountries.length > 0 ? filteredCountries.map((option) => (
            <Box
              key={option.code}
              onClick={() => {
                onChange(option);
                setPickerOpen(false);
                setCountrySearch('');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: '12px',
                py: '8px',
                cursor: 'pointer',
                bgcolor: countryCode?.code === option.code
                  ? alpha(theme.palette.primary.main, 0.08)
                  : 'transparent',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
              }}
            >
              {/* Show Emoji if supported, otherwise fallback to ISO code */}
              {checkEmojiFlagSupport() ? (
                <Typography component="span" sx={{
                  fontSize: '1.15rem', lineHeight: 1, flexShrink: 0, mt: '-2px', minWidth: 30
                }}>
                  {option.emoji}
                </Typography>
              ) : (
                <Typography component="span" sx={{
                  fontSize: '0.7rem', fontWeight: 700, minWidth: 30,
                  color: 'text.secondary', letterSpacing: '0.5px', flexShrink: 0,
                }}>
                  {option.code}
                </Typography>
              )}
              {/* Country name */}
              <Typography component="span" sx={{ flex: 1, fontSize: '0.85rem', color: 'text.primary' }}>
                {option.name}
              </Typography>
              {/* Dial code */}
              <Typography component="span" sx={{
                fontSize: '0.78rem', fontWeight: 600,
                color: 'primary.main', letterSpacing: '0.3px', flexShrink: 0,
              }}>
                {option.dial_code}
              </Typography>
            </Box>
          )) : (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No countries found</Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default CountryCodePicker;
